<script setup lang="ts">
import { computed, ref } from 'vue';
import { useLocale } from '@/plugins/translation';
import type { Exercise, Instruction } from '@ilteducation/exercise-service-types';
import { useGamesTheme } from '@ilteducation/games';

const props = defineProps<{
  exercise: Exercise;
  title: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t, l } = useLocale();

const questions = computed(() => props.exercise.content as Instruction[]);
const currentIndex = ref(0);
const showList = ref(false);

const { theme } = useGamesTheme();
</script>
<template>
  <v-theme-provider :theme="theme" with-background class="bg-surface h-100">
    <v-card variant="flat" rounded="0" class="question-display d-flex flex-column min-h-100 pa-4">
      <div class="d-flex align-center justify-space-between flex-wrap ga-4">
        <div class="d-flex align-center ga-2">
          <v-btn
            icon="fa:far fa-chevron-left"
            variant="text"
            color="on-surface"
            :aria-label="t(l.label_go_back)"
            @click="emit('close')"
          />
          <h1 id="question_display_title" class="chalkduster-font text-h5 text-sm-h4 font-weight-regular ma-0">
            {{ title }}
          </h1>
        </div>
        <v-btn
          variant="outlined"
          color="on-surface"
          height="48"
          class="text-none w-100 w-sm-auto mr-sm-16"
          @click="showList = !showList"
        >
          {{ showList ? t(l.reflection_questions.show_one_at_a_time) : t(l.reflection_questions.show_as_list) }}
          <template #append>
            <v-icon icon="fa:far fa-list-ul" class="ml-4" />
          </template>
        </v-btn>
      </div>
      <v-list v-if="showList" class="d-flex flex-column ga-12 bg-transparent px-sm-16 py-12">
        <v-list-item
          v-for="(question, index) in questions"
          :key="index"
          class="chalkboard-font text-h5 text-sm-h4 pa-0"
        >
          {{ question.description }}
        </v-list-item>
      </v-list>
      <v-container v-else max-width="940" class="d-flex flex-column justify-sm-center flex-grow-1 ga-8 px-0 py-8">
        <v-card variant="flat" min-height="300" class="question-card border-opacity-100 rounded-lg pa-4 pa-sm-8">
          <p class="chalkboard-font text-h5 text-sm-h4">{{ questions[currentIndex].description }}</p>
        </v-card>
        <div class="d-flex align-center justify-space-between flex-wrap ga-4">
          <span class="text-subtitle-1">
            {{ t(l.quiz.quiz_progress, { current: currentIndex + 1, last: questions.length }) }}
          </span>
          <div class="d-flex ga-4 ms-auto">
            <v-btn
              variant="outlined"
              color="on-surface"
              height="48"
              class="text-none"
              prepend-icon="fa:far fa-chevron-left"
              :disabled="currentIndex === 0"
              @click="currentIndex--"
            >
              {{ t(l.quiz.back) }}
            </v-btn>
            <v-btn
              variant="flat"
              color="inverse-surface"
              height="48"
              class="text-none"
              append-icon="fa:far fa-chevron-right"
              :disabled="currentIndex === questions.length - 1"
              @click="currentIndex++"
            >
              {{ t(l.quiz.next) }}
            </v-btn>
          </div>
        </div>
      </v-container>
    </v-card>
  </v-theme-provider>
</template>

<style lang="scss" scoped>
.question-display {
  background-image: var(--v-games-background-image);
  background-size: cover;
  background-attachment: fixed;
  min-height: 100%;
  font-family: 'Chalkboard', 'Comic Sans MS', sans-serif;
}

.question-card {
  background-color: rgb(var(--v-theme-surface), 0.6);
  border: 2px solid rgb(var(--v-theme-outline-variant));
}

.chalkduster-font {
  font-family: 'Chalkduster', sans-serif !important;
}

.chalkboard-font {
  font-family: 'Chalkboard', sans-serif !important;
}
</style>
